import Day from '@awesomecomponents/day-js';
import { DATE_FORMAT } from '../../utils/AppConstants';

/**
 * Returns whether member can add family member. The unique list of coverage levels of the
 * benefits passed is found in order to figure out what the max number of dependents
 * can be, for SINGLE no dependents, COUPLE 1 dependent and FAMILY any dependents.
 * Please refer to chart here for table on coverage level use cases
 * https://manulife-cdn.atlassian.net/wiki/spaces/GBMOD/pages/177278430/Member+Profile+-+Couple+Coverage
 * @param {object} allowedDependentActions
 * @param {array} features
 * @param {array} familyMembers
 * @param {array} memberBenefits
 */
export const canAddFamily = (
    allowedDependentActions,
    features,
    familyMembers,
    memberBenefits,
    studentMaxAge,
    studentMinAge
) => {
    const eligibleDependentCount = familyMembers.filter(
        (familyMember) =>
            familyMember.benefits.some((benefit) => benefit.eligibleForCoverage) ||
            isChildWithinStudentAgeRange(familyMember, studentMaxAge, studentMinAge)
    ).length;

    const doesSpouseExist = familyMembers.some(
        (familyMember) => familyMember.status === 'ACTIVE' && familyMember.familyMemberType === 'SPOUSE'
    );

    const uniqueCoverageTypes = getUniqueListOfCoverageTypes(memberBenefits);

    let coverageLevelAllowsChange;

    // Check if unique coverage types are allowed
    const allowedCoverageTypes = ['SINGLE_PLUS_MORE', 'FAMILY'];
    coverageLevelAllowsChange = uniqueCoverageTypes.every((type) => allowedCoverageTypes.includes(type));

    if (uniqueCoverageTypes.length === 1 && uniqueCoverageTypes[0] === 'COUPLE') {
        coverageLevelAllowsChange = eligibleDependentCount === 0;
    }

    return (
        ((allowedDependentActions.addSpouse && !doesSpouseExist) || allowedDependentActions.addChild) &&
        features['familyMember'] &&
        coverageLevelAllowsChange
    );
};

/**
 * Returns whether member can change dependent COB.
 * @param {object} allowedDependentActions
 * @param {array} features
 * @param {array} familyMember
 */
export const canChangeDependentCob = (allowedDependentActions, features, familyMember) => {
    const { updateSpouse: allowedToUpdateSpouse, updateChild: allowedToUpdateChild } = allowedDependentActions;
    let allowedToUpdateDependent = false;

    if (familyMember.familyMemberType === 'SPOUSE') {
        allowedToUpdateDependent = allowedToUpdateSpouse;
    }
    if (familyMember.familyMemberType === 'CHILD') {
        allowedToUpdateDependent = allowedToUpdateChild;
    }

    return allowedToUpdateDependent && features['COB'];
};
/**
 * Returns whether member's dependent is extendable.
 * @param {object} allowedDependentActions
 * @param {array} features
 * @param {array} familyMember
 */
export const isStudentStatusExtendable = (familyMember) =>
    familyMember.isStudentStatusExtendable && familyMember.specialDependent !== 'DISABLED';
/**
 * Returns whether member can extend dependent coverage.
 * @param {object} allowedDependentActions
 * @param {array} features
 * @param {array} familyMember
 */
export const canExtendDependentCoverage = (allowedDependentActions, features, familyMember) =>
    allowedDependentActions.extendStudent && features['studentStatus'] && isStudentStatusExtendable(familyMember);

/**
 * Returns a list of unique coverage types for Health and Dental benefits
 * @param {array} benefits
 */
export const getUniqueListOfCoverageTypes = (benefits) => {
    // Filter out anything that isn't Health and Dental
    const eligibleBenefits = benefits.filter((benefit) => ['EHC', 'DEN'].includes(benefit.code));

    // Strip out only the coverageType
    const benefitCoverageTypes = eligibleBenefits.map((benefit) => benefit.coverageType);

    // Create a set from coverage types and return it as an array
    return Array.from(new Set(benefitCoverageTypes));
};

/**
 * Returns active benefits that are eligible for COB based on Manuconnect indicator
 * Maps the benefits to shape required for MUX control consumption
 * @param {array} memberBenefits
 */
export const filteredCobBenefits = (memberBenefits) => {
    return memberBenefits
        .filter((benefit) => benefit.status === 'ACTIVE' && benefit.cobRequired)
        .map((benefit) => ({ id: benefit.memberBenefitId, label: benefit.name, value: benefit.code }));
};

/**
 * Returns active benefits that are eligible for COB based on Manuconnect indicator with hideCOBBenefit set to false
 * Maps the benefits to shape required for MUX control consumption
 * @param {array} memberBenefits
 */
export const cobBenefitsToDisplay = (memberBenefits) => {
    return memberBenefits
        .filter((benefit) => benefit.status === 'ACTIVE' && benefit.cobRequired && !benefit.hideCOBBenefit)
        .map((benefit) => ({ id: benefit.memberBenefitId, label: benefit.name, value: benefit.code }));
};

/**
 * Returns only family member benfits with cob property, where such benefits are eligible for COB based
 * on Manuconnect indicator with hideCOBBenefit set to false
 * @param {array} memberBenefits
 * @param {array} familyMemberBenefits
 * @returns
 */
export const familyMemberCobBenefits = (memberBenefits, familyMemberBenefits) => {
    const familyMemberBenefitsWithCob = familyMemberBenefits.filter((benefit) => benefit.cob?.hasOtherInsurer);

    const displayedMemberBenefits = memberBenefits.filter(
        (benefit) => benefit.status === 'ACTIVE' && benefit.cobRequired && !benefit.hideCOBBenefit
    );

    // Only return family member benefits that are also displayed member benefits
    return familyMemberBenefitsWithCob.filter((familyMemberBenefit) =>
        displayedMemberBenefits.some((memberBenefit) => memberBenefit.code === familyMemberBenefit.code)
    );
};

/**
 * Calculates age given a birthdate
 * @param {string} birthDate
 */
export const calculateAge = (birthDate) => Math.abs(new Day(birthDate).diff(new Day(), 'year'));

/**
 * Checks if family member is a child within the student age of the plan
 * @param {string} birthDate
 * @param {number} studentMaxAge
 * @param {number} studentMinAge
 * @param {string} familyMemberType
 */
export const isChildWithinStudentAgeRange = (familyMember, studentMaxAge, studentMinAge) => {
    const age = calculateAge(familyMember.birthDate);
    return familyMember.familyMemberType === 'CHILD' && age < studentMaxAge && age >= studentMinAge;
};

/**
 * Returns the latest of given dates. If all given dates are older than 1 year ago, it returns 1 year ago from today instead
 * @param {...string} dates - a list of one or more dates in the format 'YYYY-MM-DD'
 */
export const getCoverageDate = (...dates) => {
    let max = Day.TODAY.subtract('1', 'year');

    for (let index = 0; index < dates.length; index++) {
        const date = new Day(dates[index]);
        max = date.isAfter(max) ? date : max;
    }

    return max.format('en', DATE_FORMAT.ISO_STANDARD);
};
